import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AnalysisState } from '@aibi/contracts';
import { Badge, cn } from '@aibi/ui';
import { Filter, GripVertical } from 'lucide-react';
import { useCallback, useRef, useState, type CSSProperties } from 'react';
import { cardTitle } from '../../model/card-title';
import type { DragSource, DropTarget } from '../../model/drop-rules';
import { cardRect } from '../../model/workspace-document';
import { useUiDispatch, useUiState } from '../../state/ui-state';
import { useWorkspace } from '../../state/workspace-context';
import { dropFeedbackClass, useDropValidity } from '../dnd/drag-state';
import { CardBody } from './CardBody';
import { CardResizeHandles, type CardSizePreview } from './CardResizeHandles';
import { VisualizationToolbar } from './VisualizationToolbar';

/**
 * Tarjeta del canvas: una visualizacion (= un AnalysisState). Es arrastrable por su asa
 * (reordenar) y zona de destino para campos. Clic la selecciona para editar propiedades.
 * Los bordes permiten redimensionar ancho y alto en la rejilla.
 */
export function VisualizationCard({
  card,
  maximized = false,
}: {
  readonly card: AnalysisState;
  readonly maximized?: boolean;
}) {
  const { catalog } = useWorkspace();
  const { selectedCardId } = useUiState();
  const uiDispatch = useUiDispatch();
  const cardRef = useRef<HTMLElement | null>(null);
  const [preview, setPreview] = useState<CardSizePreview | null>(null);
  const onPreview = useCallback((next: CardSizePreview | null) => {
    setPreview(next);
  }, []);
  const source: DragSource = { type: 'card', cardId: card.id };
  const target: DropTarget = { type: 'card', cardId: card.id };
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: `card:${card.id}`, data: { source, target } });
  const valid = useDropValidity(target);
  const selected = selectedCardId === card.id;
  const title = cardTitle(card, catalog);
  const resizing = preview !== null;
  const rect = cardRect(card);

  const style = {
    ...(resizing
      ? {
          width: preview.widthPx,
          height: preview.heightPx,
          maxWidth: 'none',
          justifySelf: 'start',
          alignSelf: 'start',
          transform: `translate3d(${String(preview.offsetX)}px, ${String(preview.offsetY)}px, 0)`,
          transition: preview.settling
            ? 'width 180ms cubic-bezier(0.2, 0.8, 0.2, 1), height 180ms cubic-bezier(0.2, 0.8, 0.2, 1), transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)'
            : 'none',
          zIndex: 20,
          gridColumn: `${String(rect.x + 1)} / span ${String(rect.w)}`,
          gridRow: `${String(rect.y + 1)} / span ${String(rect.h)}`,
        }
      : {
          // El fantasma vive en DragOverlay: el original se queda como hueco fijo.
          transform: isDragging ? undefined : CSS.Translate.toString(transform),
          transition: isDragging ? undefined : transition,
          gridColumn: maximized
            ? undefined
            : `${String(rect.x + 1)} / span ${String(rect.w)}`,
          gridRow: maximized ? undefined : `${String(rect.y + 1)} / span ${String(rect.h)}`,
        }),
  } as CSSProperties;

  return (
    <article
      ref={(node) => {
        cardRef.current = node;
        setNodeRef(node);
      }}
      style={style}
      aria-label={title}
      aria-current={selected ? 'true' : undefined}
      data-card-id={card.id}
      className={cn(
        'group/card relative flex min-w-0 flex-col rounded-xl border border-border bg-card text-card-foreground shadow-card',
        maximized
          ? 'h-full overflow-hidden'
          : 'col-span-12 h-full min-h-0 overflow-visible max-md:!col-span-12 max-md:!col-start-1 max-md:!row-auto',
        !maximized && !resizing && !isDragging && 'transition-[box-shadow,opacity,transform] duration-200 ease-out',
        selected && !isDragging && 'ring-2 ring-primary',
        isDragging &&
          'z-0 border-dashed border-primary/40 bg-primary/5 opacity-40 shadow-none ring-0 [&>*]:invisible',
        resizing && 'shadow-overlay ring-2 ring-primary/50',
        dropFeedbackClass(valid, isOver),
      )}
      onClick={() => {
        uiDispatch({ type: 'select-card', cardId: card.id });
      }}
      onFocus={() => {
        uiDispatch({ type: 'select-card', cardId: card.id });
      }}
    >
      <header className="flex h-10 shrink-0 items-center gap-1 border-b border-border/70 pl-1 pr-1.5">
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label={`Mover ${title}`}
          aria-roledescription="visualizacion reposicionable"
          className="flex size-7 cursor-grab items-center justify-center rounded-md text-muted-foreground/50 outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
        >
          <GripVertical aria-hidden className="size-3.5" />
        </button>
        <h3 className="min-w-0 flex-1 truncate text-sm font-medium" title={title}>
          {title}
        </h3>
        {card.filters.length > 0 && (
          <Badge variant="outline" title={`${String(card.filters.length)} filtro(s)`}>
            <Filter aria-hidden /> {card.filters.length}
          </Badge>
        )}
        {preview !== null && (
          <span className="shrink-0 rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-primary">
            {preview.width}×{preview.height}
          </span>
        )}
        <div
          className={cn(
            'opacity-60 transition-opacity group-hover/card:opacity-100 group-focus-within/card:opacity-100',
            selected && 'opacity-100',
          )}
        >
          <VisualizationToolbar card={card} title={title} />
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-hidden rounded-b-xl">
        <CardBody card={card} title={title} />
      </div>
      <CardResizeHandles
        card={card}
        cardRef={cardRef}
        disabled={maximized || isDragging}
        visible={selected || resizing}
        onPreview={onPreview}
      />
    </article>
  );
}
